/**
 * DESIGN.md § Components `saved-view-chip`, reused here for a plain,
 * ungrouped, individually removable active filter value (spec judgement
 * call 6) — not a named/saved view, which is Story 2.4's entirely separate
 * mechanism built on the same visual token. Always rendered in its "active"
 * state (`accent-quiet`/`accent`): a chip only exists here once its filter
 * is actually applied, so there is no "at rest" state to show.
 *
 * The whole chip is one button (not a separate icon-only "×" nested inside
 * it) — its accessible name is the removal action itself
 * ("Remove filter: Role is Owner"), and the visible label plus a trailing
 * "×" both sit inside it, so there is nothing icon-only for a screen reader
 * to puzzle over. The outer button is `h-tap-target-min` (44px, the
 * accessibility floor's minimum hit area) with the `h-chip` (36px)
 * `DESIGN.md` pill nested inside it, so the *visual* chip stays exactly the
 * token's 36px while the *clickable* box meets the 44px floor — the same
 * "44×44 including the invisible hit area" pattern `icons.tsx` documents
 * for icon-only nav buttons.
 */
export function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label={`Remove filter: ${label}`}
      className="inline-flex h-tap-target-min items-center rounded-full transition-transform duration-150 ease-out active:scale-[0.96]"
    >
      <span className="inline-flex h-chip items-center gap-1 rounded-full bg-accent-quiet px-3 text-meta text-accent">
        {label}
        <span aria-hidden="true">×</span>
      </span>
    </button>
  );
}
